import {
  DiagnosticsStackGenericResponse,
  RemoteDiagnosticsSuccessResponseData,
  RemoteDiagnosticsVddGenericStructure,
  RemoteDiagnosticsVddRootMetadataResponse,
  RemoteDiagnosticsVddRootStructureResponse,
} from '@vi-iot/client';
import {
  DataKeyValues,
  LongnameValues,
  NameValues,
  PathValues,
  SemanticValues,
  TypeValues,
  VINRequestParameterValues,
} from '../../+installations/diagnostics-overlay/tabs-content/extended-parameters/diagnostics-constants';
import { DiagnosticsHelpers } from '../../+installations/diagnostics-overlay/tabs-content/extended-parameters/diagnostics-helpers';

export class VddDetailsListHelper {
  public static getFlattenedMetadata(
    metaData: RemoteDiagnosticsVddRootMetadataResponse
  ): RemoteDiagnosticsVddGenericStructure[] {
    let metaDataValues = [];
    const valueMetaData = metaData?.structure?.find(
      (structure) => structure.longname === LongnameValues.Value
    );
    if (valueMetaData && Array.isArray(valueMetaData?.structure)) {
      metaDataValues = this.getFlattenedRecursive(valueMetaData);
    }

    return metaDataValues;
  }

  public static getFlattenedSingleRead(
    singleRead: RemoteDiagnosticsSuccessResponseData
  ): RemoteDiagnosticsVddGenericStructure[] {
    let singleReadValues = [];
    if (Array.isArray(singleRead?.structure)) {
      const prefixStructure = singleRead.structure.find(
        (structureValue) => structureValue.name === NameValues.DataRecor
      );
      if (singleRead?.request_parameter_value in VINRequestParameterValues) {
        singleReadValues.push({ ...prefixStructure });
      } else if (prefixStructure) {
        singleReadValues = this.getFlattenedRecursive(prefixStructure);
      }

      return singleReadValues;
    }
  }

  public static getFlattenedStructure(
    structure: DiagnosticsStackGenericResponse<RemoteDiagnosticsVddRootStructureResponse[]>
  ): RemoteDiagnosticsVddRootStructureResponse[] {
    let structureValues = [];
    if (Array.isArray(structure?.structure?.[0]?.structure)) {
      const prefixStructure = structure.structure[0].structure.find(
        (structureValue) => structureValue.longname === LongnameValues.Structure
      );

      if (structure?.structure?.[0].request_parameter_value in VINRequestParameterValues) {
        structureValues.push({ ...prefixStructure });
      } else if (prefixStructure) {
        structureValues = this.getFlattenedRecursive(prefixStructure);
      }

      return structureValues;
    }
  }

  public static getMatchValues(
    structureValues: RemoteDiagnosticsVddGenericStructure[],
    singleReadValues: RemoteDiagnosticsVddGenericStructure[]
  ): RemoteDiagnosticsVddGenericStructure[] {
    const result: RemoteDiagnosticsVddGenericStructure[] = [];
    if (structureValues?.length && singleReadValues?.length) {
      singleReadValues.forEach((singleReadValue) => {
        if (DiagnosticsHelpers.isVddSensor(singleReadValue)) {
          this.setMatchValueStructureForSensorVdd(
            singleReadValue.structure,
            structureValues[0].structure
          );
          const additionalVal = singleReadValue.structure.find(
            (str) => str.semantic === SemanticValues.VALUE
          );
          if (additionalVal) {
            result.push({
              ...additionalVal,
            });
          }
          result.push({
            ...structureValues[0],
            isSensor: true,
          });
        } else if (singleReadValue?.semantic === SemanticValues.STARTSTOPTEMP) {
          const structureValue = structureValues.find(
            (structure) => structure.semantic === SemanticValues.STARTSTOPTEMP
          );
          const arrayOfSingleReadValueStructures = singleReadValue.structure;
          const arrayOfStructureValueStructures = structureValue.structure;

          this.setMatchValueStructureForStartStopVdd(
            arrayOfSingleReadValueStructures,
            arrayOfStructureValueStructures
          );

          result.push({ ...structureValue });
        } else if (singleReadValue?.path) {
          const path = singleReadValue?.path.replace(PathValues.DataRecor, PathValues.Struc);
          const selectedStructure = structureValues.find(
            (structureValue) => structureValue?.path === path
          );
          if (
            path === 'PA_Struc' &&
            selectedStructure.type === TypeValues.eTABLE &&
            selectedStructure.structure.length === 6 &&
            selectedStructure.value === ''
          ) {
            const matchedValue = { ...selectedStructure };
            matchedValue.structure.forEach((structure) => {
              structure.value = singleReadValue.structure.find(
                (singleRead) =>
                  singleRead.path.replace(PathValues.DataRecor, PathValues.Struc) === structure.path
              )?.value;
            });
            result.push(matchedValue);
          } else if (
            selectedStructure &&
            Object.keys(selectedStructure).some((key) => key === DataKeyValues.Value)
          ) {
            const matchedValue = { ...selectedStructure };
            matchedValue.value = singleReadValue?.value;
            result.push(matchedValue);
          }
        } else if (singleReadValue?.semantic === SemanticValues.DESCRIPTION) {
          result.push(singleReadValue);
        }
      });
    }

    return result;
  }

  public static getUpdatedSingleReadValues(
    path: string,
    newValue: string,
    singleReadValues: RemoteDiagnosticsSuccessResponseData
  ): RemoteDiagnosticsSuccessResponseData {
    const updatedSingleReadValues = { ...singleReadValues };

    if (Array.isArray(updatedSingleReadValues.structure)) {
      const dataRecorStructure = DiagnosticsHelpers.getDataRecorStructure(updatedSingleReadValues);
      if (dataRecorStructure) {
        const updatedPath = path.replace(PathValues.Struc, PathValues.DataRecor);
        this.setNewValueRecursive(updatedPath, newValue, dataRecorStructure);
      }
    }

    return updatedSingleReadValues;
  }

  public static getPayloadForVddIsReadFalse(
    path: string,
    newValue: string,
    structureValues: DiagnosticsStackGenericResponse<RemoteDiagnosticsVddRootStructureResponse[]>,
    payloadForVddIsReadFalse: RemoteDiagnosticsVddGenericStructure
  ): RemoteDiagnosticsVddGenericStructure {
    const structureProperties = structureValues?.structure[0];
    const updatedStructure = payloadForVddIsReadFalse?.structure[2];
    const structurePropertiesInStructure = updatedStructure
      ? updatedStructure?.structure
      : structureProperties?.structure[1]?.structure;
    const updatedStructureValues = {
      request_parameter_name: structureProperties?.request_parameter_name,
      service: structureProperties?.service,
      ecu: structureProperties?.ecu,
      request_parameter_value: structureProperties?.request_parameter_value,
      type: 'ReadVddDataByVdd',
      structure: [
        {
          name: 'PA_ServiID',
          value: '98',
        },
        {
          name: 'PA_DataIdent',
          value: 'SetDefaultValues',
        },
        {
          path: 'PA_DataRecor',
          structure: VddDetailsListHelper.setNewValueForIsReadFalse(
            path,
            newValue,
            structurePropertiesInStructure
          ),
        },
      ],
    };

    return updatedStructureValues;
  }

  private static setNewValueForIsReadFalse(
    path: string,
    newValue: string,
    structure: RemoteDiagnosticsVddGenericStructure[]
  ): RemoteDiagnosticsVddGenericStructure[] {
    interface updatedValue {
      indexForChange: number;
      objWithNewValue: RemoteDiagnosticsVddGenericStructure;
    }

    let updatedStructure: RemoteDiagnosticsVddGenericStructure[];

    const replaceOldObjToObjWithUpdatedData = (
      originalArray: RemoteDiagnosticsVddGenericStructure[],
      indexToReplace: number,
      newArrValue: RemoteDiagnosticsVddGenericStructure
    ): RemoteDiagnosticsVddGenericStructure[] => {
      return originalArray.map((value, index) => (index === indexToReplace ? newArrValue : value));
    };

    const updateValueInProperObj = (
      objWithOldValue: RemoteDiagnosticsVddGenericStructure,
      index: number
    ): updatedValue => {
      for (let key in objWithOldValue) {
        if (objWithOldValue[key] === path) {
          return {
            indexForChange: index,
            objWithNewValue: { ...objWithOldValue, value: newValue },
          };
        }
      }
    };

    structure?.map((objWithOldValue, index, structure) => {
      const updatedValueInProperObj = updateValueInProperObj(objWithOldValue, index);
      if (updatedValueInProperObj) {
        updatedStructure = replaceOldObjToObjWithUpdatedData(
          structure,
          updatedValueInProperObj?.indexForChange,
          updatedValueInProperObj?.objWithNewValue
        );
      }
    });

    return updatedStructure;
  }

  private static setNewValueRecursive(
    path: string,
    newValue: string,
    singleReadValuesStructure: RemoteDiagnosticsVddGenericStructure
  ): void {
    if (Array.isArray(singleReadValuesStructure?.structure)) {
      singleReadValuesStructure.structure.forEach((structure) => {
        if (Object.keys(structure).some((key) => key === DataKeyValues.Semantic)) {
          this.setNewValueRecursive(path, newValue, structure);
        } else if (singleReadValuesStructure?.path === path) {
          singleReadValuesStructure.value = newValue;
        }
      });
    } else if (singleReadValuesStructure?.path === path) {
      singleReadValuesStructure.value = newValue;
    }
  }

  private static setMatchValueStructureForSensorVdd(
    arrayOfSingleReadValueStructures: RemoteDiagnosticsVddGenericStructure[],
    arrayOfStructureValueStructures: RemoteDiagnosticsVddGenericStructure[]
  ): void {
    arrayOfSingleReadValueStructures.forEach((singleReadStr, index) => {
      arrayOfStructureValueStructures[index].value = singleReadStr.value;
    });
  }

  private static setMatchValueStructureForStartStopVdd(
    arrayOfSingleReadValueStructures: RemoteDiagnosticsVddGenericStructure[],
    arrayOfStructureValueStructures: RemoteDiagnosticsVddGenericStructure[]
  ): void {
    arrayOfSingleReadValueStructures.forEach((structure, index) => {
      const startTimeSingleRead = this.findParametersBySemantics(
        structure,
        SemanticValues.STARTTIME
      );
      const stopTimeSingleRead = this.findParametersBySemantics(structure, SemanticValues.STOPTIME);
      const valueSingleRead = this.findParametersBySemantics(structure, SemanticValues.VALUE);
      const enumChoiceSingleRead = this.findParametersBySemantics(
        structure,
        SemanticValues.ENUMCHOICE
      );

      const startTimeStructure = this.findParametersBySemantics(
        arrayOfStructureValueStructures[index],
        SemanticValues.STARTTIME
      );
      const stopTimeStructure = this.findParametersBySemantics(
        arrayOfStructureValueStructures[index],
        SemanticValues.STOPTIME
      );

      if (valueSingleRead && enumChoiceSingleRead) {
        const valueStructure = this.findParametersBySemantics(
          arrayOfStructureValueStructures[index],
          SemanticValues.VALUE
        );
        const enumChoiceStructure = this.findParametersBySemantics(
          arrayOfStructureValueStructures[index],
          SemanticValues.ENUMCHOICE
        );

        valueStructure.value = valueSingleRead.value;
        enumChoiceStructure.value = enumChoiceSingleRead.value;
      }

      this.updateTimeStructure(startTimeSingleRead, startTimeStructure);
      this.updateTimeStructure(stopTimeSingleRead, stopTimeStructure);
    });
  }

  private static updateTimeStructure(
    singleReadStructure: RemoteDiagnosticsVddGenericStructure,
    targetStructure: RemoteDiagnosticsVddGenericStructure
  ): void {
    const hourSingleRead = this.findParametersBySemantics(singleReadStructure, SemanticValues.HOUR);
    const minuteSingleRead = this.findParametersBySemantics(
      singleReadStructure,
      SemanticValues.MINUTE
    );

    const hourTargetStructure = this.findParametersBySemantics(
      targetStructure,
      SemanticValues.HOUR
    );

    if (hourTargetStructure) {
      hourTargetStructure.value = hourSingleRead?.value;
    }

    const minuteTargetStructure = this.findParametersBySemantics(
      targetStructure,
      SemanticValues.MINUTE
    );

    if (minuteTargetStructure) {
      minuteTargetStructure.value = minuteSingleRead?.value;
    }
  }

  private static findParametersBySemantics(
    structure: RemoteDiagnosticsVddGenericStructure,
    semantic: SemanticValues
  ): RemoteDiagnosticsVddGenericStructure {
    return structure?.structure?.find((element) => element.semantic === semantic);
  }

  private static getFlattenedRecursive(
    prefixStructure: RemoteDiagnosticsVddGenericStructure
  ): RemoteDiagnosticsVddGenericStructure[] {
    const resultArray: RemoteDiagnosticsVddGenericStructure[] = [];

    if (Array.isArray(prefixStructure?.structure)) {
      let parentAdded = false;
      let descriptionAdded = false;

      const isSensor = DiagnosticsHelpers.isVddSensor(prefixStructure);

      if (isSensor) {
        resultArray.push(prefixStructure);
      } else {
        prefixStructure.structure.forEach((structureElement) => {
          if (Object.keys(structureElement).some((key) => key === DataKeyValues.Semantic)) {
            if (structureElement.semantic === SemanticValues.STARTSTOPTEMP) {
              resultArray.push({
                semantic: SemanticValues.DESCRIPTION,
                translationid: structureElement.translationid,
              });
              resultArray.push(structureElement);
            } else {
              if (prefixStructure.translationid && !descriptionAdded) {
                descriptionAdded = true;
                resultArray.push({
                  semantic: SemanticValues.DESCRIPTION,
                  translationid: prefixStructure.translationid,
                });
              }
              resultArray.push(...this.getFlattenedRecursive(structureElement));
            }
          } else if (!parentAdded) {
            parentAdded = true;
            resultArray.push(prefixStructure);
          }
        });
      }
    } else {
      resultArray.push(prefixStructure);
    }

    return resultArray;
  }
}
